import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
  Fade,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { RowSelectionConfig } from '../../../../../services/Main/types.Component';
import useStyles from './TableSelectionPanel.styles';
import { useFormatMessage } from '../../../../../locale';
import TableSelectionPanelMobile from './TableSelectionPanel.Mobile';
import TableSelectionPanelDesktop from './TableSelectionPanel.Desktop';
import { useSelectionActions } from './useSelectionActions';

interface TableSelectionPanelProps {
  selectedRowIds: (string | number)[];
  rowSelectionConfig?: RowSelectionConfig;
  toggleSelection: (args: { rowIds: (string | number)[] }) => void;
}

const TableSelectionPanel = ({
  selectedRowIds,
  rowSelectionConfig,
  toggleSelection,
}: TableSelectionPanelProps) => {
  const { isSelectionLoading, selectionRowActions } = useSelectionActions(
    selectedRowIds,
    rowSelectionConfig
  );

  const classes = useStyles();
  const formatMessage = useFormatMessage();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  if (selectedRowIds.length === 0) {
    return null;
  }

  const renderEmptyState = () => {
    if (
      (!selectionRowActions || selectionRowActions.length === 0) &&
      !isSelectionLoading
    ) {
      return (
        <Typography variant="body1" className={classes.empty}>
          {formatMessage('noAvailableActions')}
        </Typography>
      );
    }

    if (
      (!selectionRowActions || selectionRowActions.length === 0) &&
      isSelectionLoading
    ) {
      return (
        <Typography variant="body1">{formatMessage('loading')}</Typography>
      );
    }

    return null;
  };

  const handleDeselectAll = () => {
    toggleSelection({ rowIds: selectedRowIds });
  };

  return (
    <Fade in={selectedRowIds.length > 0} timeout={150}>
      <Paper className={classes.panel} elevation={0}>
        <Box className={classes.selectedContainer}>
          <Tooltip title={formatMessage('deselectAll')} arrow>
            <IconButton size="small" onClick={handleDeselectAll}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body1">
            {`${formatMessage('selected')}: ${selectedRowIds.length}`}
          </Typography>
        </Box>
        <Divider
          orientation={isSmDown ? 'horizontal' : 'vertical'}
          flexItem={!isSmDown}
          className={classes.divider}
        />
        {renderEmptyState()}
        {selectionRowActions && selectionRowActions.length > 0 && (
          <>
            {isSmDown ? (
              <TableSelectionPanelMobile
                actions={selectionRowActions}
                disabled={isSelectionLoading}
              />
            ) : (
              <TableSelectionPanelDesktop
                actions={selectionRowActions}
                disabled={isSelectionLoading}
              />
            )}
          </>
        )}
      </Paper>
    </Fade>
  );
};

export default TableSelectionPanel;
